import nodeFetch from 'node-fetch';
import { ApolloClient, ApolloLink, InMemoryCache, from } from '@apollo/client';
import { createUploadLink } from 'apollo-upload-client';
import { onError } from '@apollo/client/link/error';
import i18n from 'src/i18n';
import config from 'src/config/apollo.json';

interface UploadFetchOptions {
	method: string;
	headers: Record<string, any>;
	body: Document | BodyInit;
	useUpload: boolean;
	onProgress: (this: XMLHttpRequest, ev: ProgressEvent<EventTarget>) => any;
}

async function uploadFetch(url: string, options: UploadFetchOptions) {
	return await new Promise((resolve, reject) => {
		const xhr = new XMLHttpRequest();
		xhr.open(options.method || 'get', url);
		xhr.withCredentials = true;

		Object.keys(options.headers || {}).forEach((key) => {
			xhr.setRequestHeader(key, options.headers[key]);
		});

		xhr.onload = (e: any) => {
			resolve({
				ok: true,
				text: () => e.target.responseText,
				json: () => JSON.parse(e.target.responseText),
			});
		};

		xhr.onerror = reject;

		if (xhr.upload) {
			xhr.upload.onprogress = options.onProgress;
		}

		xhr.send(options.body as XMLHttpRequestBodyInit);
	});
}

async function customFetch(url: string, options: UploadFetchOptions) {
	if (options.useUpload) {
		return await uploadFetch(url, options);
	}

	return nodeFetch(url, options);
}

const createApolloClient: any = (req) => {
	const isSSR = !!req;
	const globalContext = typeof window === 'undefined' ? (global as any) : (window as any);

	const authLink = new ApolloLink((operation, forward) => {
		operation.setContext(() => {
			const headers: any = {
				'x-pharket-site': 'pharket',
			};

			let accessToken;
			if (isSSR) {
				accessToken = req.cookie?.user_access_token;
				headers.Origin = req.header('host');
			} else {
				accessToken = localStorage.getItem('user_access_token');
			}

			if (accessToken) {
				headers.Authorization = `Bearer ${accessToken}`;
			}

			headers.lang = req?.language || i18n.language;

			return {
				headers,
			};
		});

		return forward(operation);
	});

	// TODO: improve error handling
	const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
		if (graphQLErrors?.[0]?.message === 'Unauthorized') {
			// toast.warning('خطایی رخ داده است', 'ابتدا وارد حساب کاربری خود شوید');
		} else if (graphQLErrors?.[0].message === 'Forbidden') {
			// toast.error('خطایی رخ داده است', 'شما مجاز به انجام این کار نیستید');
		} else if (graphQLErrors) {
			// toast.error('خطایی رخ داده است', 'خطای غیرمنتظره‌ای رخ داده است');
		}

		if (networkError) {
			// toast.error('خطایی رخ داده است', 'خطای اتصال به شبکه');
		}

		forward(operation);
	});

	const httpLink = createUploadLink({
		includeExtensions: true,
		uri: `${isSSR ? process.env.SSR_API_URL : process.env.API_URL}${config.path}`,
		credentials: config.credentials ? 'include' : 'omit',
		fetch: customFetch,
	} as any);

	let cache = new InMemoryCache({
		possibleTypes: [],
	} as any);

	if (!isSSR && globalContext.APOLLO_STATE) {
		cache = cache.restore(globalContext.APOLLO_STATE);
	}

	return new ApolloClient({
		ssrMode: isSSR,
		link: from([authLink, errorLink, httpLink]),
		cache,
	});
};

export default createApolloClient;
